import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { cn } from '@/lib/utils'

interface NavItem {
    label: string
    href: string
}

const TOP_LINKS: NavItem[] = [
    { label: 'home', href: '/' },
    { label: 'coaching', href: '/what-we-do' },
    { label: 'team', href: '/who-we-are' },
    { label: 'articles', href: '/insights' },
    { label: 'client stories', href: '/showcases' }
]

export const Navbar: FC = (): ReactNode => {
    const router = useRouter()
    const [mobileOpen, setMobileOpen] = useState(false)

    // Close menus on navigation.
    useEffect(() => {
        setMobileOpen(false)
    }, [router.asPath])

    // Lock body scroll while the mobile menu is open.
    useEffect(() => {
        document.body.classList.toggle('mdl-no-scroll', mobileOpen)
        return () => document.body.classList.remove('mdl-no-scroll')
    }, [mobileOpen])

    const isActive = (href: string): boolean => router.pathname === href

    return (
        <header
            className={cn('navbar mdl-nav', mobileOpen && 'mdl-mobile-open')}
            role='banner'
        >
            <div className='container nav-container'>
                <div className='nav-menu-container'>
                    <Link href='/' className='brand-link brand-lockup mdl-nav-brand' aria-label='Czechmate Consulting home'>
                        <img src='/images/czechmate/logo-mark.png' alt='' className='brand-logo brand-mark' width={32} height={30} />
                        <img src='/images/czechmate/logo-wordmark.png' alt='Czechmate Consulting' className='brand-logo' width={150} height={29} />
                    </Link>

                    <nav role='navigation' className='nav-menu mdl-nav-menu'>
                        {TOP_LINKS.map((item) => (
                            <div className='nav-link-holder' key={item.href + item.label}>
                                <Link
                                    href={item.href}
                                    className={cn('nav-link mdl-inline-block', isActive(item.href) && 'mdl-current')}
                                    aria-current={isActive(item.href) ? 'page' : undefined}
                                >
                                    <div>{item.label}</div>
                                    <div className='link-background' />
                                </Link>
                            </div>
                        ))}

                        <div className='nav-link-holder nav-button-holder'>
                            <Link href='/contact' className='button nav-button mdl-inline-block'>
                                <div>let&apos;s talk</div>
                            </Link>
                        </div>
                    </nav>

                    <div className='nav-menu-overlay-mobile' onClick={() => setMobileOpen(false)} />

                    <div
                        className={cn('menu-button mdl-nav-button', mobileOpen && 'mdl-open')}
                        onClick={() => setMobileOpen((open) => !open)}
                        role='button'
                        aria-label='Menu'
                        tabIndex={0}
                    >
                        <img src='/images/menu-icon.svg' loading='lazy' alt='Menu' className='menu-icon' />
                    </div>
                </div>
            </div>
        </header>
    )
}
